import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PERSONALITIES, AUTONOMY_LEVELS, TOOLS, ROLE_SUGGESTIONS } from "@/lib/lifeos";
import { UserPlus } from "lucide-react";

const empty = {
  name: "", codename: "", department_id: "", role: "",
  personality: "Analytical", autonomy: "Semi-Autonomous", goals: "", schedule: "", connected_tools: [],
};

const fieldCls = "bg-[#0b0e14] border-white/10 focus-visible:ring-cyan-400/40 text-zinc-100";
const selectCls = "w-full bg-[#0b0e14] border border-white/10 rounded-md px-3 h-10 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40";

export default function HireAgentModal({ open, onOpenChange, departments, presetDept, onHire }) {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...empty, department_id: presetDept || departments[0]?.id || "" });
    }
  }, [open, presetDept, departments]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTool = (t) =>
    setForm((f) => ({ ...f, connected_tools: f.connected_tools.includes(t) ? f.connected_tools.filter((x) => x !== t) : [...f.connected_tools, t] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.role.trim() || !form.department_id) return;
    setSubmitting(true);
    try {
      await onHire(form);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0b0e14] border border-cyan-500/40 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto thin-scroll shadow-[0_0_50px_rgba(0,240,255,0.18)]">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-widest text-cyan-300 flex items-center gap-2">
            <UserPlus size={18} /> Hire AI Agent
          </DialogTitle>
          <DialogDescription className="text-zinc-500 font-mono text-xs">
            Configure a new autonomous worker and deploy it into your headquarters.
          </DialogDescription>
        </DialogHeader>

        <form data-testid="hire-agent-form" onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Agent Name *</Label>
              <Input data-testid="hire-agent-name-input" className={fieldCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. LifeOS" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Codename</Label>
              <Input data-testid="hire-agent-codename-input" className={fieldCls} value={form.codename} onChange={(e) => set("codename", e.target.value)} placeholder="e.g. ATLAS-01" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Department *</Label>
              <select data-testid="hire-agent-department-select" className={selectCls} value={form.department_id} onChange={(e) => set("department_id", e.target.value)}>
                {departments.map((d) => (
                  <option key={d.id} value={d.id} className="bg-[#0b0e14]">{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Role *</Label>
              <Input data-testid="hire-agent-role-input" list="role-suggestions" className={fieldCls} value={form.role} onChange={(e) => set("role", e.target.value)} placeholder="e.g. Marketing Manager" />
              <datalist id="role-suggestions">
                {ROLE_SUGGESTIONS.map((r) => <option key={r} value={r} />)}
              </datalist>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Personality</Label>
              <select className={selectCls} value={form.personality} onChange={(e) => set("personality", e.target.value)}>
                {PERSONALITIES.map((p) => <option key={p} value={p} className="bg-[#0b0e14]">{p}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Autonomy Level</Label>
              <select className={selectCls} value={form.autonomy} onChange={(e) => set("autonomy", e.target.value)}>
                {AUTONOMY_LEVELS.map((a) => <option key={a} value={a} className="bg-[#0b0e14]">{a}</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Goals & Responsibilities</Label>
            <Textarea className={fieldCls} rows={2} value={form.goals} onChange={(e) => set("goals", e.target.value)} placeholder="What should this agent achieve?" />
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Schedule</Label>
            <Input className={fieldCls} value={form.schedule} onChange={(e) => set("schedule", e.target.value)} placeholder="e.g. 09:00 daily briefing, then continuous" />
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Connected Apps</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {TOOLS.map((t) => {
                const on = form.connected_tools.includes(t);
                return (
                  <button
                    type="button"
                    key={t}
                    data-testid={`tool-chip-${t}`}
                    onClick={() => toggleTool(t)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-mono border transition-all ${on ? "bg-cyan-500/15 text-cyan-300 border-cyan-400/40" : "bg-white/[0.03] text-zinc-500 border-white/10 hover:text-zinc-300"}`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-zinc-400 hover:text-zinc-200">Cancel</Button>
            <Button
              data-testid="hire-agent-submit-button"
              type="submit"
              disabled={submitting}
              className="bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-tactical font-bold uppercase tracking-wider shadow-[0_0_20px_rgba(0,240,255,0.35)]"
            >
              {submitting ? "Deploying..." : "Deploy Agent"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
